import { ref, reactive, computed } from '@vue/composition-api'
import axiosIns from '@/libs/axios'
import { errorNotification, successNotification } from '@/utils/toast-notifications'
import formatDate from '@/utils/date-format'

export default function useBenefitsList(ctx) {
  const rawList = ref([])
  const loading = ref(false)

  const queryParams = reactive({
    page: 1,
    perPage: 10,
    sortBy: 'created_at',
    sortDesc: true,
  })

  const tableColumns = [
    {
      key: 'name.en',
      label: 'Benefit Name',
      sortable: true,
      formatter: (value, key, item) => item.name.en || '-',
    },
    {
      key: 'benefit_type',
      label: 'Type',
      formatter: val => val.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    },
    {
      key: 'config.rides_limit',
      label: 'Usage Limit',
      formatter: (value, key, item) => item.config?.rides_limit ?? '-',
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      formatter: val => (val ? 'Active' : 'Inactive'),
    },
    {
      key: 'created_at',
      label: 'Created Date',
      sortable: true,
      formatter: val => formatDate(val),
    },
    'actions',
  ]

  const fetchBenefits = async () => {
    loading.value = true
    try {
      const { data } = await axiosIns.get('/subscriptions/benefits')
      rawList.value = data.data.benefits
    } catch (err) {
      errorNotification('Failed to load benefits')
    } finally {
      loading.value = false
    }
  }

  const sortedList = computed(() => {
    const sorted = [...rawList.value]
    const key = queryParams.sortBy
    const desc = queryParams.sortDesc

    sorted.sort((a, b) => {
      let aVal; let
        bVal

      if (key === 'name.en') {
        aVal = a.name.en
        bVal = b.name.en
      } else if (key === 'is_active') {
        aVal = a.is_active ? 1 : 0
        bVal = b.is_active ? 1 : 0
      } else {
        aVal = a[key]
        bVal = b[key]
      }

      if (aVal < bVal) return desc ? 1 : -1
      if (aVal > bVal) return desc ? -1 : 1
      return 0
    })

    return sorted
  })

  const paginatedList = computed(() => {
    const start = (queryParams.page - 1) * queryParams.perPage
    return sortedList.value.slice(start, start + queryParams.perPage)
  })

  const total = computed(() => rawList.value.length)

  fetchBenefits()

  const selectedBenefit = ref(null)

  const viewBenefit = benefit => {
    selectedBenefit.value = benefit
    ctx.root.$bvModal.show('view-benefit-modal')
  }

  const editBenefit = benefit => {
    selectedBenefit.value = benefit
    ctx.root.$bvModal.show('create-benefit-modal')
  }

  const confirmDelete = benefit => {
    selectedBenefit.value = benefit
    ctx.root.$bvModal.show('delete-benefit-modal')
  }

  const deletingBenefit = ref(false)
  const deleteBenefit = async () => {
    try {
      deletingBenefit.value = true
      await axiosIns.delete(`/subscriptions/benefits/${selectedBenefit.value.id}`)
      successNotification('Benefit deleted successfully')
      fetchBenefits()
    } catch (err) {
      errorNotification('Failed to delete benefit')
    } finally {
      deletingBenefit.value = false
      ctx.root.$bvModal.hide('delete-benefit-modal')
    }
  }
  const addBenefitHandler = () => {
    selectedBenefit.value = null
    ctx.root.$bvModal.show('create-benefit-modal')
  }
  return {
    deletingBenefit,
    addBenefitHandler,
    viewBenefit,
    formatDate,
    editBenefit,
    confirmDelete,
    deleteBenefit,
    selectedBenefit,
    list: paginatedList,
    total,
    loading,
    queryParams,
    tableColumns,
    fetchBenefits,
  }
}
